
// src/components/library/AudioPlayer.jsx
import React from 'react';
import { useAudio } from '../../context/AudioContext';

const AudioPlayer = () => {
  const { 
    currentTrack, 
    isPlaying, 
    duration, 
    currentTime, 
    volume,
    togglePlay, 
    seek, 
    setVolume 
  } = useAudio();
  
  // Helper function to format time
  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  if (!currentTrack) return null;
  
  return (
    <div className="bg-gray-900 border-t border-gray-700 py-4 px-6 shadow-lg">
      <div className="flex flex-col md:flex-row items-center">
        <div className="flex items-center mb-4 md:mb-0 md:w-1/4">
          <div className="w-12 h-12 mr-4 flex-shrink-0">
          </div>
          <div>
            <h4 className="text-gray-100 font-medium truncate max-w-xs">{currentTrack.title}</h4>
          </div>
        </div>
        
        <div className="w-full md:w-2/4 px-4">
          <div className="flex items-center justify-center mb-2">
            <button 
              className="mx-2 text-gray-400 hover:text-white focus:outline-none"
              aria-label="Previous track"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={togglePlay}
              className="mx-2 w-12 h-12 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center focus:outline-none"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                </svg>
              )}
            </button>
            
            <button 
              className="mx-2 text-gray-400 hover:text-white focus:outline-none"
              aria-label="Next track"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="flex items-center">
            <span className="text-xs text-gray-400 w-10 text-right">{formatTime(currentTime)}</span>
            <div className="flex-grow mx-3">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime || 0}
                onChange={(e) => seek(parseFloat(e.target.value))}
                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <span className="text-xs text-gray-400 w-10">{formatTime(duration)}</span>
          </div>
        </div>
        
        <div className="w-full md:w-1/4 flex items-center justify-end mt-4 md:mt-0">
          <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m0 0l-2.828 2.828-4.242-4.242 2.828-2.828m0 0a5 5 0 017.072 0" />
          </svg>
          <div className="w-24">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;